import {
  DragHandle as DragHandleIcon,
  ImageOutlined as ImageOutlinedIcon,
  RepeatOutlined as RepeatOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import type { Theme } from '@mui/material/styles';
import {
  Accordion,
  ACCORDION_VARIANT,
  Checkbox,
  ImageBox,
  Modal,
  Radio,
  Switch,
  theme as dsTheme,
  Tooltip,
} from '@sb/cc-design-system';
import { type SyntheticEvent, useCallback, useMemo, useState } from 'react';
import SortableList, { SortableItem, SortableKnob } from 'react-easy-sort';
import placeholderImage from 'src/assets/images/placeholder.png';
import { Pagination } from 'src/components/pagination/Pagination';
import { useModal } from 'src/hooks/useModal';
import { IGcfProductListProductImage } from 'src/interfaces';
import { lang } from 'src/utils';
import { getThumbnailSrc } from 'src/utils/gcf-helper';

import { DEFAULT_ACCORDION_STYLE } from '../../../../constants';
import {
  type IAttributeFlavor,
  NONE_LABEL,
} from '../../ProductOverrideRightSideBar.helper';
import { getReplaceModalAssets } from './ProductAttributeAccordion.helper';
import type { IProductAttributeAccordion } from './ProductAttributeAccordion.interface';
import {
  FlavorDragHandle,
  FlavorRow,
  ImageAccordionWrapper,
  ImagePreview,
  ImageSection,
  ProductHeader,
  ShowImageRow,
  SizeAccordionWrapper,
  SizeHeaderRow,
  StyledFlavorRight,
} from './ProductAttributeAccordion.styled';

const DraggedSizeItemGlobalStyles = (
  <GlobalStyles
    styles={(theme: Theme) => ({
      '.size-dragged-item': {
        borderLeft: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        opacity: 0.8,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        zIndex: 9999,
        overflow: 'visible',
      },
    })}
  />
);

const ITEMS_PER_PAGE = 8;

export const ProductAttributeAccordion = (
  props: IProductAttributeAccordion,
) => {
  const {
    sizes,
    defaultSize,
    defaultFlavor,
    showImage,
    defaultImage,
    productVariationsAssets,
    onChange,
    onDefaultFlavorChange,
    onDefaultSizeChange,
    onShowImageChange,
    onProductImageChange,
  } = props;

  const [replaceAssetName, setReplaceAssetName] = useState<
    string | undefined
  >();
  const [page, setPage] = useState(1);

  const defaultSizeObj = useMemo(
    () => sizes.find((s) => s.name === defaultSize),
    [sizes, defaultSize],
  );

  const imageReplaceModal = useModal();

  const replaceModalAssets = getReplaceModalAssets(
    productVariationsAssets,
    defaultFlavor,
    defaultImage,
  );
  const totalPages = Math.ceil(replaceModalAssets.length / ITEMS_PER_PAGE);
  const paginatedAssets = replaceModalAssets.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE,
  );

  const handleSizeSortEnd = useCallback(
    (oldIndex: number, newIndex: number) => {
      const next = [...sizes];
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      onChange(next.map((s, i) => ({ ...s, order: i })));
    },
    [sizes, onChange],
  );

  const handleFlavorCheck = (sizeName: string, flavorName: string) => {
    const next = sizes.map((size) => {
      if (size.name !== sizeName) return size;

      return {
        ...size,
        flavors: size.flavors.map((f) => {
          if (f.name === flavorName) {
            return { ...f, checked: !f.checked };
          }

          return f;
        }),
      };
    });
    onChange(next);

    if (sizeName === defaultSize) {
      const updatedSize = next.find((s) => s.name === sizeName);
      const selectedFlavorJustUnchecked = updatedSize?.flavors.find(
        (f) =>
          f.name === flavorName &&
          !f.checked &&
          f.productCode === defaultFlavor,
      );

      if (selectedFlavorJustUnchecked) {
        const hasAnyChecked = updatedSize?.flavors.some((f) => f.checked);

        if (!hasAnyChecked) {
          onDefaultSizeChange(undefined, next);
        }
      }
    }
  };

  const handleFlavorRadioClick = (
    sizeName: string,
    flavor: IAttributeFlavor,
  ) => {
    if (sizeName === defaultSize && flavor.productCode === defaultFlavor) {
      return;
    }

    // Build next with the flavor checked, then pass to both handlers
    // so handleDefaultFlavorChange doesn't use stale localAttributes
    let next = sizes;
    if (!flavor.checked) {
      next = sizes.map((size) => {
        if (size.name !== sizeName) return size;

        return {
          ...size,
          flavors: size.flavors.map((f) => {
            if (f.name === flavor.name) {
              return { ...f, checked: true };
            }

            return f;
          }),
        };
      });
    }

    onDefaultFlavorChange(sizeName, flavor.productCode, next);
  };

  const handleFlavorSortEnd = useCallback(
    (sizeName: string, oldIndex: number, newIndex: number) => {
      const next = sizes.map((size) => {
        if (size.name !== sizeName) return size;

        const flavors = [...size.flavors];
        const [moved] = flavors.splice(oldIndex, 1);
        flavors.splice(newIndex, 0, moved);

        return {
          ...size,
          flavors: flavors.map((f, i) => ({ ...f, order: i })),
        };
      });
      onChange(next);
    },
    [sizes, onChange],
  );

  const FlavorCheckbox = ({
    flavor,
    sizeName,
  }: {
    flavor: IAttributeFlavor;
    sizeName: string;
  }) => {
    return (
      <>
        <Checkbox
          checked={flavor.checked}
          onChange={() => handleFlavorCheck(sizeName, flavor.name)}
          aria-label={`Toggle flavor ${flavor.name || NONE_LABEL}`}
        />
        <Tooltip showOnOverflowOnly title={flavor.name || NONE_LABEL}>
          <span className="AccordionItemLabel">
            {flavor.name || NONE_LABEL}
          </span>
        </Tooltip>
      </>
    );
  };

  const handleShowImageChange = (_: SyntheticEvent, checked: boolean) => {
    onShowImageChange(checked);
  };

  const handleReplaceImage = () => {
    setPage(1);
    setReplaceAssetName(undefined);
    imageReplaceModal.onOpenModal();
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setReplaceAssetName(undefined);
  };

  const handleConfirm = () => {
    const updatedFlavorAssetMap: Record<string, IGcfProductListProductImage[]> =
      {};
    const next = sizes.map((size) => {
      if (size.name !== defaultSize) return size;

      return {
        ...size,
        flavors: size.flavors.map((f) => {
          if (f.productCode === defaultFlavor) {
            const updatedFlavor = {
              ...f,
              images: f.images?.map((img) => {
                if (img.visible) {
                  return { ...img, assetName: replaceAssetName };
                }

                return img;
              }),
            };

            updatedFlavorAssetMap[updatedFlavor.productCode] =
              updatedFlavor.images ?? [];

            return updatedFlavor;
          }

          return f;
        }),
      };
    });
    onProductImageChange(next, updatedFlavorAssetMap);

    imageReplaceModal.onCloseModal();
  };

  const imagePreviewSrc = !showImage
    ? ''
    : (defaultImage?.assetName && getThumbnailSrc(defaultImage.assetName)) ||
      placeholderImage;

  return (
    <>
      {DraggedSizeItemGlobalStyles}
      <SizeAccordionWrapper>
        <ProductHeader aria-label={lang.scheduleComponent.preview.product}>
          {lang.scheduleComponent.preview.product}
        </ProductHeader>
        <SortableList
          onSortEnd={handleSizeSortEnd}
          className="size-sort-list"
          draggedItemClassName="size-dragged-item">
          {sizes.map((size) => {
            return (
              <SortableItem key={size.key}>
                <div>
                  <Accordion
                    showBorder
                    showBorderRadius={false}
                    title={
                      <SizeHeaderRow component="span">
                        <SortableKnob>
                          <IconButton
                            size="small"
                            disableRipple
                            sx={{
                              padding: 0,
                              cursor: 'grab',
                              color: 'text.secondary',
                            }}
                            aria-label="Drag to reorder">
                            <DragHandleIcon fontSize="small" />
                          </IconButton>
                        </SortableKnob>
                        <span>{size.name || NONE_LABEL}</span>
                        {size.flavors.some((f) =>
                          f.images?.some((img) => img.visible),
                        ) && (
                          <ImageOutlinedIcon
                            fontSize="small"
                            sx={{
                              marginLeft: 'auto',
                              color: dsTheme.palette.text.secondary,
                            }}
                          />
                        )}
                      </SizeHeaderRow>
                    }
                    variant={ACCORDION_VARIANT.leftArrow}
                    defaultExpanded={size.key === defaultSizeObj?.key}
                    {...DEFAULT_ACCORDION_STYLE}>
                    <SortableList
                      onSortEnd={(oldIndex, newIndex) =>
                        handleFlavorSortEnd(size.name, oldIndex, newIndex)
                      }>
                      {size.flavors.map((flavor) => (
                        <SortableItem key={flavor.key}>
                          <FlavorRow>
                            <SortableKnob>
                              <FlavorDragHandle
                                size="small"
                                disableRipple
                                aria-label="Drag to reorder">
                                <DragHandleIcon fontSize="small" />
                              </FlavorDragHandle>
                            </SortableKnob>
                            <FlavorCheckbox
                              flavor={flavor}
                              sizeName={size.name}
                            />
                            <StyledFlavorRight>
                              <Radio
                                size="small"
                                checked={
                                  size.name === defaultSize &&
                                  flavor.productCode === defaultFlavor
                                }
                                onClick={() =>
                                  handleFlavorRadioClick(size.name, flavor)
                                }
                                aria-label={`Select ${
                                  flavor.name || NONE_LABEL
                                } as default image`}
                              />
                            </StyledFlavorRight>
                          </FlavorRow>
                        </SortableItem>
                      ))}
                    </SortableList>
                  </Accordion>
                </div>
              </SortableItem>
            );
          })}
        </SortableList>
        <ImageAccordionWrapper
          aria-label={lang.scheduleComponent.preview.productImage}>
          <Accordion
            title={
              <SizeHeaderRow component="span">
                <span>{lang.scheduleComponent.preview.productImage}</span>
              </SizeHeaderRow>
            }
            variant={ACCORDION_VARIANT.leftArrow}
            {...DEFAULT_ACCORDION_STYLE}>
            <ImageSection>
              <ShowImageRow>
                <Switch
                  checked={showImage}
                  onChange={handleShowImageChange}
                  aria-label={lang.scheduleComponent.preview.showImage}
                />
                <span>{lang.scheduleComponent.preview.showImage}</span>
                <IconButton
                  size="small"
                  sx={{ marginLeft: 'auto' }}
                  data-testid="swap-image-button"
                  disabled={!showImage || !replaceModalAssets?.length}
                  onClick={handleReplaceImage}>
                  <RepeatOutlinedIcon fontSize="small" />
                </IconButton>
              </ShowImageRow>
              {showImage && (
                <ImagePreview
                  data-testid="product-attribute-image-preview"
                  src={imagePreviewSrc}
                  alt=""
                  onError={(event: SyntheticEvent<HTMLImageElement>) => {
                    if (!event.currentTarget.dataset.fallbackApplied) {
                      event.currentTarget.dataset.fallbackApplied = 'true';
                      event.currentTarget.src = placeholderImage;
                    }
                  }}
                />
              )}
            </ImageSection>
          </Accordion>
        </ImageAccordionWrapper>
      </SizeAccordionWrapper>

      {imageReplaceModal.open && (
        <Modal
          width={1024}
          open={imageReplaceModal.open}
          onClose={imageReplaceModal.onCloseModal}
          onCancelAction={imageReplaceModal.onCloseModal}
          onConfirmAction={handleConfirm}
          title={lang.replaceProductImage}
          confirmButtonProps={{
            disabled: !replaceAssetName,
          }}
          cancelButtonText={lang.cancel}
          confirmButtonText={lang.select}>
          <Box display="flex" flexWrap="wrap" gap={2} padding={2}>
            {paginatedAssets.map((asset) => (
              <ImageBox
                key={asset.id}
                size={238}
                active={replaceAssetName === asset.name}
                clickable={true}
                src={getThumbnailSrc(asset.name)}
                onClick={() => setReplaceAssetName(asset.name)}
              />
            ))}
          </Box>
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </Modal>
      )}
    </>
  );
};
